import { useEffect, useState } from 'react'
import { HeroIcons } from '../../lib/utils/heroIcons'
import useLocale from '../../hooks/useLocale'
import routes from '../../config/route'
import Link from './Link'
import { ConsentChoice } from '../../lib/cookieConsent'

const COPY = {
  fr: {
    title: 'On respecte votre vie privée',
    description:
      'Nous utilisons des cookies pour mesurer notre audience et améliorer votre expérience.',
    learnMore: 'En savoir plus',
    accept: 'Tout accepter',
    decline: 'Refuser',
  },
  en: {
    title: 'We value your privacy',
    description:
      'We use cookies to measure our audience and improve your experience.',
    learnMore: 'Learn more',
    accept: 'Accept all',
    decline: 'Decline',
  },
}

export default function CookieConsent({
  onDecision,
}: {
  onDecision: (choice: ConsentChoice) => void
}) {
  const { locale } = useLocale()
  const [isVisible, setIsVisible] = useState(false)
  const copy = COPY[locale?.startsWith('fr') ? 'fr' : 'en']

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsVisible(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  const handleDecision = (choice: ConsentChoice) => {
    // Play the exit transition, then let the parent unmount the banner.
    setIsVisible(false)
    window.setTimeout(() => onDecision(choice), 300)
  }

  return (
    <div
      role="dialog"
      aria-label={copy.title}
      className={`fixed inset-x-0 bottom-0 z-[1000] flex transform-gpu justify-center px-3 pb-3 transition-all duration-500 ease-out sm:px-6 sm:pb-6 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
      }`}
    >
      <div className="relative mx-auto w-full max-w-4xl overflow-hidden rounded-container border border-white/65 bg-[linear-gradient(145deg,rgba(255,255,255,0.94)_0%,rgba(255,255,255,0.82)_45%,rgba(225,233,255,0.92)_100%)] p-4 shadow-[0_26px_58px_rgba(20,15,72,0.3)] backdrop-blur-2xl backdrop-saturate-150 sm:p-6 desktop:max-w-5xl">
        <div className="pointer-events-none absolute -left-16 -top-20 h-44 w-44 rounded-full bg-[radial-gradient(circle,rgba(182,196,255,0.4)_0%,transparent_72%)]" />
        <div className="pointer-events-none absolute -bottom-24 -right-16 h-56 w-56 rounded-full bg-[radial-gradient(circle,rgba(170,152,255,0.34)_0%,transparent_74%)]" />

        <div className="relative z-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
          <div className="flex items-start gap-3 sm:gap-4">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-inner-card bg-gradient text-white shadow-[0_10px_24px_rgba(51,40,154,0.28)] sm:h-12 sm:w-12">
              <HeroIcons.ShieldCheckIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </span>
            <div>
              <p className="text-shelters-body font-semibold text-[#17213d] sm:text-shelters-lead">
                {copy.title}
              </p>
              <p className="mt-1 text-shelters-body leading-[1.5] text-[#3a4577]">
                {copy.description}{' '}
                <Link
                  href={routes.privacyPolicy}
                  className="font-medium text-sheltersBlue underline underline-offset-2"
                >
                  {copy.learnMore}
                </Link>
              </p>
            </div>
          </div>

          <div className="flex shrink-0 flex-col-reverse gap-3 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={() => handleDecision('denied')}
              className="inline-flex h-5 w-full items-center justify-center px-5 text-[#3a4577] text-shelters-hero-action sm:h-12 sm:w-auto"
            >
              {copy.decline}
            </button>
            <button
              type="button"
              onClick={() => handleDecision('granted')}
              className="inline-flex h-11 w-full items-center justify-center rounded-button bg-gradient px-7 font-semibold text-white shadow-[0_14px_30px_rgba(51,40,154,0.4)] ring-2 ring-sheltersBlue/15 transition-all duration-300 text-shelters-hero-action hover:-translate-y-0.5 hover:shadow-[0_18px_36px_rgba(51,40,154,0.5)] hover:brightness-110 sm:h-12 sm:w-auto"
            >
              {copy.accept}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
